// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

import logger from "./utils/logger";

export const PWA_UPDATE_EVENT = "pwa-update-available";
export const PWA_UPDATE_RECOVERY_EVENT = "pwa-update-recovery";
export const APP_REFRESH_GUARD_SESSION_KEY = "pwa-refresh-guard";

const APP_REFRESH_DISCOVERY_TIMEOUT_MS = 8000;
let pendingPwaUiState = null;
const pwaUiStateListeners = new Set();

export const isExpectedServiceWorkerRegistrationError = (
  error,
  userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "",
  isSecureContext = typeof window !== "undefined" ? window.isSecureContext : true,
) => {
  const message = (error?.message || "").toLowerCase();
  const agent = (userAgent || "").toLowerCase();

  return (
    !isSecureContext ||
    message.includes("rejected") ||
    message.includes("denied") ||
    message.includes("not allowed") ||
    message.includes("unsupported") ||
    agent.includes("wv") ||
    agent.includes("webview")
  );
};

const notifyPwaUiStateListeners = () => {
  pwaUiStateListeners.forEach((listener) => {
    try {
      listener(pendingPwaUiState);
    } catch (error) {
      logger.warn("PWA: pending UI state listener failed", error);
    }
  });
};

const publishPendingPwaUiState = (nextState) => {
  pendingPwaUiState = nextState;
  notifyPwaUiStateListeners();
  return nextState;
};

export const getPendingPwaUiState = () => pendingPwaUiState;

export const clearPendingPwaUiState = () => {
  pendingPwaUiState = null;
  notifyPwaUiStateListeners();
};

export const subscribeToPwaUiState = (listener) => {
  if (typeof listener !== "function") {
    return () => {};
  }

  pwaUiStateListeners.add(listener);

  return () => {
    pwaUiStateListeners.delete(listener);
  };
};

const readRefreshGuard = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = window.sessionStorage.getItem(APP_REFRESH_GUARD_SESSION_KEY);

    return value ? JSON.parse(value) : null;
  } catch (error) {
    logger.warn("PWA: failed to read refresh guard", error);
    return null;
  }
};

const writeRefreshGuard = (payload) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      APP_REFRESH_GUARD_SESSION_KEY,
      JSON.stringify(payload),
    );
  } catch (error) {
    logger.warn("PWA: failed to write refresh guard", error);
  }
};

const buildRefreshUrl = (reason) => {
  const nextUrl = new URL(window.location.href);

  nextUrl.searchParams.set("updated", Date.now().toString());

  if (reason) {
    nextUrl.searchParams.set("update_reason", reason);
  }

  return nextUrl.toString();
};

export const getAppRefreshGuard = () => readRefreshGuard();

export const setAppRefreshGuard = (payload) => {
  writeRefreshGuard(payload);
};

export const clearAppRefreshGuard = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(APP_REFRESH_GUARD_SESSION_KEY);
  } catch (error) {
    logger.warn("PWA: failed to clear refresh guard", error);
  }
};

export const reloadAppForUpdate = ({
  reason = "update_available",
  forceRetry = false,
} = {}) => {
  if (typeof window === "undefined") {
    return { attempted: false, skipped: true };
  }

  const existingGuard = readRefreshGuard();
  if (existingGuard && !forceRetry) {
    logger.info("PWA: reload skipped due to session guard", existingGuard);
    return { attempted: false, skipped: true };
  }

  writeRefreshGuard({
    reason,
    at: Date.now(),
  });

  window.location.replace(buildRefreshUrl(reason));

  return { attempted: true, skipped: false };
};

const attachActivationReload = (worker, reason) => {
  if (!worker) {
    return false;
  }

  if (worker.state === "activated") {
    clearRefreshRecoveryTimer(worker.__pwaRegistration || null);
    reloadAppForUpdate({ reason });
    return true;
  }

  if (worker.__pwaActivationReloadAttached) {
    return true;
  }

  const handleStateChange = (event) => {
    if (event.target.state === "activated") {
      worker.removeEventListener("statechange", handleStateChange);
      worker.__pwaActivationReloadAttached = false;
      clearRefreshRecoveryTimer(worker.__pwaRegistration || null);
      reloadAppForUpdate({ reason });
    }
  };

  worker.__pwaActivationReloadAttached = true;
  worker.addEventListener("statechange", handleStateChange);

  return true;
};

const requestWaitingWorkerActivation = (worker, reason) => {
  if (!worker) {
    return false;
  }

  attachActivationReload(worker, reason);

  if (worker.state !== "activated") {
    worker.postMessage({ type: "SKIP_WAITING" });
  }

  return true;
};

const watchInstallingWorker = (worker, registration, reason) => {
  if (!worker) {
    return false;
  }

  if (worker.__pwaInstallingWatcherAttached) {
    return true;
  }

  worker.__pwaRegistration = registration;

  const handleStateChange = () => {
    if (worker.state === "installed") {
      requestWaitingWorkerActivation(registration.waiting || worker, reason);
      return;
    }

    if (worker.state === "activated") {
      worker.removeEventListener("statechange", handleStateChange);
      worker.__pwaInstallingWatcherAttached = false;
      clearRefreshRecoveryTimer(registration);
      reloadAppForUpdate({ reason });
    }
  };

  worker.__pwaInstallingWatcherAttached = true;
  worker.addEventListener("statechange", handleStateChange);

  return true;
};

const watchRegistrationForRefresh = (registration, reason) => {
  if (!registration) {
    return false;
  }

  if (requestWaitingWorkerActivation(registration.waiting, reason)) {
    clearRefreshRecoveryTimer(registration);
    return true;
  }

  if (watchInstallingWorker(registration.installing, registration, reason)) {
    scheduleRefreshRecovery(registration, reason);
    return true;
  }

  if (!registration.__pwaUpdateFoundListenerAttached) {
    registration.__pwaUpdateFoundListenerAttached = true;
    registration.addEventListener("updatefound", () => {
      clearRefreshRecoveryTimer(registration);
      watchInstallingWorker(registration.installing, registration, reason);
    });
  }

  scheduleRefreshRecovery(registration, reason);

  registration.update().catch((error) => {
    clearRefreshRecoveryTimer(registration);
    dispatchUpdateRecovery({ reason });
    logger.warn("PWA: update check failed during refresh", error);
  });

  return true;
};

export const dispatchUpdateAvailable = ({
  waitingWorker = null,
  compatibilityState = null,
  isForced = false,
  reason = "update_available",
  showUpdating = false,
} = {}) => {
  const detail = publishPendingPwaUiState({
    waitingWorker,
    compatibilityState,
    isForced,
    reason,
    showUpdating,
    isRecovery: false,
  });

  window.dispatchEvent(
    new CustomEvent(PWA_UPDATE_EVENT, {
      detail,
    }),
  );
};

export const dispatchUpdateRecovery = ({ reason = "update_available" } = {}) => {
  const detail = publishPendingPwaUiState({
    waitingWorker: null,
    compatibilityState: null,
    isForced: false,
    reason,
    showUpdating: false,
    isRecovery: true,
  });

  window.dispatchEvent(
    new CustomEvent(PWA_UPDATE_RECOVERY_EVENT, {
      detail,
    }),
  );
};

const clearRefreshRecoveryTimer = (registration) => {
  if (!registration?.__pwaRefreshRecoveryTimer) {
    return;
  }

  window.clearTimeout(registration.__pwaRefreshRecoveryTimer);
  registration.__pwaRefreshRecoveryTimer = null;
};

const scheduleRefreshRecovery = (registration, reason) => {
  if (!registration || registration.__pwaRefreshRecoveryTimer) {
    return;
  }

  registration.__pwaRefreshRecoveryTimer = window.setTimeout(() => {
    registration.__pwaRefreshRecoveryTimer = null;
    dispatchUpdateRecovery({ reason });
  }, APP_REFRESH_DISCOVERY_TIMEOUT_MS);
};

export const requestAppRefresh = async ({
  registration = null,
  waitingWorker = null,
  reason = "update_available",
} = {}) => {
  if (typeof window === "undefined") {
    return { attempted: false, skipped: true };
  }

  let activeRegistration = registration;
  if (!activeRegistration && "serviceWorker" in navigator) {
    try {
      activeRegistration = await navigator.serviceWorker.getRegistration();
    } catch (error) {
      logger.warn("PWA: failed to resolve active registration", error);
    }
  }

  const targetWorker = waitingWorker || activeRegistration?.waiting || null;

  if (targetWorker) {
    requestWaitingWorkerActivation(targetWorker, reason);
    return { attempted: true, skipped: false };
  }

  if (activeRegistration) {
    watchRegistrationForRefresh(activeRegistration, reason);
    return { attempted: true, skipped: false };
  }

  return reloadAppForUpdate({ reason });
};

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === "[::1]" ||
  // 127.0.0.0/8 are considered localhost for IPv4.
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
  ),
);

export function register(config) {
  if ("serviceWorker" in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener("load", () => {
      const swUrl = `${process.env.PUBLIC_URL}/app-sw.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          logger.info(
            "PWA: cache-first service worker. Learn more: https://cra.link/PWA",
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              logger.info(
                "PWA: new content available. Close tabs to update. https://cra.link/PWA",
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              logger.info("PWA: content cached for offline use");

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isBotReader =
        userAgent.includes("google-read-aloud") ||
        userAgent.includes("bot") ||
        userAgent.includes("crawler") ||
        userAgent.includes("spider");
      const isRestrictedWebview =
        userAgent.includes("wv") ||
        userAgent.includes("webview") ||
        !window.isSecureContext;
      const isExpectedRegistrationError =
        isExpectedServiceWorkerRegistrationError(
          error,
          navigator.userAgent,
          window.isSecureContext,
        );

      if (isBotReader || isRestrictedWebview || isExpectedRegistrationError) {
        logger.warn("PWA: service worker skipped", {
          reason: isBotReader
            ? "bot_or_reader"
            : isExpectedRegistrationError
              ? "expected_registration_error"
              : "restricted_webview",
          user_agent: navigator.userAgent,
          is_secure_context: window.isSecureContext,
          error_message: error?.message,
        });
      } else {
        logger.error("PWA: service worker registration error", error, {
          user_agent: navigator.userAgent,
          is_secure_context: window.isSecureContext,
          origin: window.location.origin,
        });
      }
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        window.location.reload();
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch((error) => {
      logger.warn("PWA: offline mode, no internet connection", error);
    });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        logger.error("PWA: unregister error", error);
      });
  }
}
